import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { mono, borderSoft } from '../../styles/techComponents';

/* ============================================================
   Gráfico embebido en una respuesta del agente. El modelo emite
   un bloque ```grafico con JSON y acá se dibuja.
   Formato: { tipo, titulo, datos: [{ nombre, valor }] }
   ============================================================ */

const COLORS = ['#00B4D8', '#06D6A0', '#FFD166', '#EF476F', '#A4B0C2', '#7C8AA0'];
const tick = { fontSize: 11, fontFamily: mono, fill: 'var(--text-disabled)' };
const tooltipStyle = {
  background: 'rgba(11, 19, 32, 0.95)',
  border: '1px solid rgba(0, 180, 216, 0.4)',
  borderRadius: 4,
  fontFamily: mono,
  fontSize: 12,
  color: 'var(--text-primary)',
};

// El JSON lo escribe un modelo, así que puede venir roto o con otra forma.
// Si no se puede dibujar, se muestra el bloque crudo en vez de romper el chat.
function parseSpec(raw) {
  let spec;
  try { spec = JSON.parse(raw); } catch { return null; }
  if (!spec || typeof spec !== 'object') return null;

  const datos = (Array.isArray(spec.datos) ? spec.datos : [])
    .map((d) => ({
      nombre: String(d?.nombre ?? d?.name ?? ''),
      valor: Number(d?.valor ?? d?.value),
    }))
    .filter((d) => d.nombre && Number.isFinite(d.valor));

  if (datos.length === 0) return null;
  return { tipo: spec.tipo || 'barras', titulo: spec.titulo || '', datos };
}

const Wrap = ({ titulo, children }) => (
  <div style={{
    border: `1px solid ${borderSoft}`,
    borderRadius: 8,
    padding: '0.8rem 0.6rem 0.4rem',
    margin: '0.8rem 0',
    background: 'rgba(11, 19, 32, 0.45)',
  }}>
    {titulo && (
      <p style={{
        margin: '0 0 0.5rem 0.4rem',
        fontFamily: mono,
        fontSize: '0.66rem',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--text-disabled)',
      }}>
        {titulo}
      </p>
    )}
    <div style={{ height: 240 }}>{children}</div>
  </div>
);

export default function AgentChart({ raw }) {
  const spec = parseSpec(raw);

  if (!spec) {
    return (
      <pre style={{
        border: `1px solid ${borderSoft}`, borderRadius: 8, padding: '0.7rem',
        fontSize: '0.75rem', color: 'var(--text-disabled)', overflowX: 'auto',
      }}>
        {raw}
      </pre>
    );
  }

  const { tipo, titulo, datos } = spec;

  if (tipo === 'torta') {
    return (
      <Wrap titulo={titulo}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={datos} dataKey="valor" nameKey="nombre" outerRadius={85} label>
              {datos.map((d, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontFamily: mono, fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      </Wrap>
    );
  }

  if (tipo === 'lineas') {
    return (
      <Wrap titulo={titulo}>
        <ResponsiveContainer>
          <LineChart data={datos} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="nombre" stroke="var(--text-disabled)" tick={tick} />
            <YAxis stroke="var(--text-disabled)" tick={tick} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Line type="monotone" dataKey="valor" stroke={COLORS[0]} strokeWidth={2.4} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Wrap>
    );
  }

  // Por defecto: barras.
  return (
    <Wrap titulo={titulo}>
      <ResponsiveContainer>
        <BarChart data={datos} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
          <XAxis dataKey="nombre" stroke="var(--text-disabled)" tick={tick} />
          <YAxis stroke="var(--text-disabled)" tick={tick} allowDecimals={false} />
          <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0, 180, 216, 0.06)' }} />
          <Bar dataKey="valor">
            {datos.map((d, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Wrap>
  );
}
